import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../Auth/AuthContext";
import BASE_URL from "../../../config/config";
import DOMPurify from "dompurify";
import "../../../css/UserCourseDetail.css";
import CommentsSection from "../../page/CommentsSection";
import CoursePurchase from "../../page/CoursePurchase";

// --- کامپوننت لیست جلسات ---
const CoursePlayer = ({ sessions, onLessonSelect }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">
      <div className="bg-gray-50 border-t border-gray-200 h-full overflow-y-auto p-4">
        <h3 className="text-xl font-bold text-dark mb-4">سرفصل‌های دوره</h3>
        <ul className="space-y-2">
          {sessions.map((session) => {
            const lesson = {
              id: session.id,
              title: session.title,
              description: session.description || "توضیحاتی در دسترس نیست.",
              videoUrl: session.file_url?.endsWith(".mp4")
                ? `${BASE_URL}/get-media?file_id=${encodeURIComponent(
                    session.file_url
                  )}&type=file`
                : null,
            };
            return (
              <li key={session.id}>
                <button
                  onClick={() => onLessonSelect(lesson)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-gray-100 text-gray-700 border border-gray-200 transition"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm">
                      {lesson.videoUrl ? "🎥" : "📄"}
                    </span>
                    <span className="text-sm truncate">{lesson.title}</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {session.duration || "ناشناخته"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// --- کامپوننت اصلی ---
const UserCourseDetail = () => {
  const [course, setCourse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [courseStatus, setCourseStatus] = useState(""); // 'completed', 'pending', 'not_enrolled'
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const { user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();

  // محاسبه قیمت نهایی با تخفیف
  const calculateFinalPrice = (price, discount) => {
    const originalPrice = parseFloat(price) || 0;
    const discountPercent = parseFloat(discount) || 0;
    return originalPrice - (originalPrice * discountPercent) / 100;
  };

  const [activeTab, setActiveTab] = useState("curriculum");

  const getLevelText = (level) => {
    switch (level) {
      case "beginner":
        return "مقدماتی";
      case "intermediate":
        return "متوسط";
      case "advanced":
        return "پیشرفته";
      default:
        return "نامشخص";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user) {
          setError("برای مشاهده جزئیات دوره ابتدا وارد شوید.");
          setLoading(false);
          return;
        }

        const courseResponse = await fetch(
          `${BASE_URL}/user/courses/${courseId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        const responseData = await courseResponse.json();
        if (!courseResponse.ok) {
          throw new Error(responseData.message || "دسترسی به دوره ممکن نیست.");
        }

        setCourse(responseData.data);
        setCourseStatus(responseData.enrollmentStatus); // مثلاً: 'completed', 'pending', 'not_enrolled'

        // فقط در صورتی که دوره تکمیل شده باشد، جلسات را بارگیری کن
        if (responseData.enrollmentStatus === "completed") {
          const sessionsResponse = await fetch(
            `${BASE_URL}/courses/${courseId}/sessions`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
              credentials: "include",
            }
          );

          if (!sessionsResponse.ok) {
            const errorData = await sessionsResponse.json();
            throw new Error(
              errorData.message || "دریافت جلسات با خطا مواجه شد."
            );
          }

          const sessionsData = await sessionsResponse.json();
          setSessions(
            sessionsData.data.filter((session) => session.is_available)
          );
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, courseId]);

  const handlePurchaseClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setShowPurchaseModal(true);
  };

  const getTabClass = (tabName) => {
    return `px-6 py-3 font-bold transition ${
      activeTab === tabName
        ? "text-primary border-b-2 border-primary"
        : "text-gray-600 hover:text-primary"
    }`;
  };

  const formatPrice = () => {
    if (course.is_free || course.price === "0") {
      return { display: "رایگان", isFree: true };
    }

    const discount = parseFloat(course.discount) || 0;
    const originalPrice = parseFloat(course.price);

    if (discount > 0) {
      return {
        display: "با تخفیف",
        isFree: false,
        hasDiscount: true,
        originalPrice: originalPrice.toLocaleString(),
        finalPrice: calculateFinalPrice(
          course.price,
          course.discount
        ).toLocaleString(),
        discountPercent: discount,
      };
    }

    return {
      display: originalPrice.toLocaleString(),
      isFree: false,
      hasDiscount: false,
    };
  };

  const formatDuration = () => {
    return `${course.duration || "2 ساعت"} ویدیو`;
  };

  if (loading) return <div className="p-8 text-center">در حال بارگذاری...</div>;
  if (error) return <div className="p-8 text-red-600 text-center">{error}</div>;
  if (!course)
    return <div className="p-8 text-center">اطلاعات دوره یافت نشد.</div>;

  const renderTabContent = () => {
    switch (activeTab) {
      case "curriculum":
        return (
          <CoursePlayer sessions={sessions} onLessonSelect={setCurrentLesson} />
        );
      case "description":
        return (
          <div
            className="text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(course.description),
            }}
          />
        );
      case "resources":
        return (
          <div className="space-y-4">
            <p className="text-gray-600">
              منابع آموزشی در دسترس قرار خواهند گرفت.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-light font-vazirmatn">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-primary/10 py-2">
        <div className="container mx-auto px-4">
          <nav className="flex items-center text-sm text-gray-600">
            <a href="/dashboard" className="hover:text-primary transition">
              🏠 خانه
            </a>
            <span className="mx-2 text-gray-400">←</span>
            <a href="/courses" className="hover:text-primary transition">
              دوره‌ها
            </a>
            <span className="mx-2 text-gray-400">←</span>
            <span className="text-primary font-medium">{course.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Course Video */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
                <div className="relative bg-black h-64 md:h-96">
                  {courseStatus === "completed" ? (
                    currentLesson ? (
                      currentLesson.videoUrl ? (
                        <video
                          src={currentLesson.videoUrl}
                          controls
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-white text-xl">
                          این جلسه ویدئوی ندارد.
                        </div>
                      )
                    ) : (
                      <div className="flex items-center justify-center h-full text-white text-6xl">
                        ▶️
                      </div>
                    )
                  ) : courseStatus === "pending" ? (
                    <div className="flex flex-col items-center justify-center h-full text-white p-6 text-center">
                      <div className="text-6xl mb-4">⏳</div>
                      <h2 className="text-2xl font-bold mb-2">
                        سفارش شما در حال بررسی است
                      </h2>
                      <p className="text-lg mb-4">
                        پرداخت شما ثبت شده و در حال تأیید توسط تیم پشتیبانی است.
                        <br />
                        لطفاً تا تکمیل فرآیند صبر کنید.
                      </p>
                      <button
                        onClick={() => navigate("/dashboard/orders")}
                        className="bg-gray-700 text-white px-6 py-3 rounded-xl font-bold text-lg hover:bg-gray-800 transition"
                      >
                        مشاهده سفارشات من
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white p-6 text-center">
                      <div className="text-6xl mb-4">🔒</div>
                      <h2 className="text-2xl font-bold mb-2">
                        دوره قفل شده است
                      </h2>
                      <p className="text-lg mb-4">
                        برای دسترسی به محتوای این دوره، باید آن را خریداری کنید.
                      </p>
                      <button
                        onClick={handlePurchaseClick}
                        className="bg-primary text-white px-6 py-3 rounded-xl font-bold text-lg hover:bg-dark transition"
                      >
                        خرید دوره
                      </button>
                    </div>
                  )}
                </div>

                {currentLesson && courseStatus === "completed" && (
                  <div className="p-4">
                    <h2 className="text-lg font-bold text-dark">
                      {currentLesson.title}
                    </h2>
                    <div
                      className="text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(currentLesson.description),
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Course Title */}
              <h1 className="text-3xl font-bold text-dark mb-4">
                {course.title}
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {course.short_description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-primary">
                    ⭐ {course.rating || "5.0"}
                  </div>
                  <div className="text-sm text-gray-600">امتیاز</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-primary">
                    👥 {course.students_count || 3}
                  </div>
                  <div className="text-sm text-gray-600">دانشجو</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-primary">
                    ⏱️ {course.duration || "۰ ساعت"}
                  </div>
                  <div className="text-sm text-gray-600">مدت زمان</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-primary">
                    📊 {getLevelText(course.level) || "مقدماتی"}
                  </div>
                  <div className="text-sm text-gray-600">سطح</div>
                </div>
              </div>

              {/* Instructor */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl">
                    👨‍💻
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-dark">
                      {course.instructor_name || "مربی هوش جدید"}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {course.instructor_bio || "بیوگرافی مربی"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              {courseStatus === "completed" ? (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="flex border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab("curriculum")}
                      className={getTabClass("curriculum")}
                    >
                      📚 سرفصل‌ها
                    </button>
                    <button
                      onClick={() => setActiveTab("description")}
                      className={getTabClass("description")}
                    >
                      📝 توضیحات
                    </button>
                    <button
                      onClick={() => setActiveTab("resources")}
                      className={getTabClass("resources")}
                    >
                      📎 منابع
                    </button>
                  </div>
                  <div className="p-6">{renderTabContent()}</div>
                </div>
              ) : courseStatus === "pending" ? (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-6 text-center">
                  <h3 className="text-xl font-bold text-dark mb-4">
                    🕒 سفارش شما در حال بررسی است
                  </h3>
                  <p className="text-gray-600 mb-4">
                    پس از تأیید پرداخت، به‌طور خودکار به محتوای دوره دسترسی پیدا
                    خواهید کرد.
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/courses")}
                    className="bg-gray-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-800 transition"
                  >
                    مشاهده جزئیات سفارش
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-6 text-center">
                  <h3 className="text-xl font-bold text-dark mb-4">
                    برای مشاهده سرفصل‌ها و محتوای دوره، لطفاً آن را خریداری
                    کنید.
                  </h3>
                  <button
                    onClick={handlePurchaseClick}
                    className="bg-primary text-white px-6 py-3 rounded-xl font-bold text-lg hover:bg-dark transition"
                  >
                    خرید دوره
                  </button>
                </div>
              )}

              {/* Comments Section */}
              <div className="comments-section mt-6">
                <h3 className="text-2xl font-bold gradient-text mb-6">
                  سوالات دانشجویان
                </h3>
                <div id="commentsList">
                  <CommentsSection courseId={courseId} />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-transparent hover:border-primary/30 transition">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">🎓</div>
                  <div className="text-2xl font-bold text-primary mb-2">
                    {(() => {
                      const priceInfo = formatPrice();

                      if (priceInfo.isFree) {
                        return (
                          <div className="text-3xl font-bold text-green-600">
                            رایگان
                          </div>
                        );
                      }

                      if (priceInfo.hasDiscount) {
                        return (
                          <div className="price-container-sidebar">
                            <div className="discount-badge-sidebar">
                              {priceInfo.discountPercent}% تخفیف
                            </div>
                            <div className="original-price-sidebar">
                              {priceInfo.originalPrice} تومان
                            </div>
                            <div className="final-price-sidebar">
                              {priceInfo.finalPrice} تومان
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="text-2xl font-bold text-primary">
                          {priceInfo.display} تومان
                        </div>
                      );
                    })()}
                  </div>
                  {course.old_price && (
                    <div className="text-sm text-gray-500 line-through">
                      {course.old_price} تومان
                    </div>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  {[
                    { icon: "⏱️", text: formatDuration() },
                    { icon: "📱", text: "دسترسی موبایل" },
                    { icon: "♾️", text: "دسترسی مادام‌العمر" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>

                {courseStatus === "completed" ? (
                  <button className="w-full bg-primary text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-dark transition mb-3 shadow-lg">
                    🎓 شروع یادگیری
                  </button>
                ) : courseStatus === "pending" ? (
                  <button
                    onClick={() => navigate("/dashboard/orders")}
                    className="w-full bg-gray-500 text-white px-6 py-4 rounded-xl font-bold text-lg"
                  >
                    ⏳ در انتظار تأیید پرداخت...
                  </button>
                ) : (
                  <button
                    onClick={handlePurchaseClick}
                    className="w-full bg-primary text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-dark transition mb-3 shadow-lg"
                  >
                    🛒 خرید دوره
                  </button>
                )}

                <button className="w-full border border-primary text-primary px-6 py-4 rounded-xl font-bold hover:bg-primary hover:text-white transition">
                  ❤️ افزودن به علاقه‌مندی‌ها
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <CoursePurchase
          courseId={course.id}
          onClose={() => setShowPurchaseModal(false)}
          isOpen={showPurchaseModal}
        />
      )}
    </div>
  );
};

export default UserCourseDetail;
